/**
 * The jumps of a bar, as MusicXML: the segno, the coda sign, and the words between them.
 *
 * MusicXML says each of these twice, and both have to be written. `<direction-type>` is
 * what is printed -- a glyph for the two signs, words for the three instructions -- and
 * `<sound>` is what is played, which is the only part a sequencer reads. A file that
 * prints `D.S. al Coda` and carries no `dalsegno` attribute is drawn right and played
 * straight through.
 *
 * The words are worked out here rather than stored, because they are not a choice: a jump
 * in a piece that has a Fine is `al Fine` and one in a piece that has a coda is `al Coda`.
 * See {@link ../document/types#JumpMarks}.
 *
 * Like the tempo and the rehearsal mark, these are written into the leading part only:
 * they belong to the piece, and writing them into every part would have the importer read
 * the same jump once per player.
 */

import type { GlobalMeasure, Score } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";

/** Whether any bar of the piece carries a sign, which is what the words depend on. */
function somewhere(score: Score, sign: "fine" | "coda"): boolean {
  return score.measures.some((measure) => measure.jump?.[sign] === true);
}

/** What a D.C. or a D.S. is printed as in this piece. */
function wordsFor(score: Score, jump: "daCapo" | "dalSegno"): string {
  const head = jump === "daCapo" ? "D.C." : "D.S.";
  if (somewhere(score, "fine")) return `${head} al Fine`;
  if (somewhere(score, "coda")) return `${head} al Coda`;
  return head;
}

/** One direction that is both drawn and played. */
function direction(printed: XmlNode, sound: Readonly<Record<string, string>>): XmlNode {
  return el("direction", { placement: "above" }, [
    el("direction-type", {}, [printed]),
    el("sound", sound),
  ]);
}

/**
 * The signs standing at the head of a bar: the segno and the coda.
 *
 * They are destinations rather than instructions, so they are written before the notes of
 * the bar -- a D.S. comes back to the start of the bar the sign stands on, not to the
 * middle of it.
 */
export function jumpsBefore(global: GlobalMeasure): XmlNode[] {
  const jump = global.jump;
  if (jump === null) return [];
  return [
    jump.segno ? direction(el("segno"), { segno: "segno" }) : null,
    jump.coda ? direction(el("coda"), { coda: "coda" }) : null,
  ].filter((node): node is XmlNode => node !== null);
}

/**
 * The instructions standing at the end of a bar: To Coda, Fine, and the jump itself.
 *
 * These are written after the notes, because that is where they are obeyed and where an
 * engraver draws them: the bar is played and only then does the player go elsewhere.
 */
export function jumpsAfter(score: Score, global: GlobalMeasure): XmlNode[] {
  const jump = global.jump;
  if (jump === null) return [];
  return [
    jump.toCoda ? direction(leaf("words", "To Coda"), { tocoda: "coda" }) : null,
    jump.fine ? direction(leaf("words", "Fine"), { fine: "yes" }) : null,
    jump.jump === null
      ? null
      : direction(
          leaf("words", wordsFor(score, jump.jump)),
          jump.jump === "daCapo" ? { dacapo: "yes" } : { dalsegno: "segno" },
        ),
  ].filter((node): node is XmlNode => node !== null);
}
